// @ts-nocheck
/**
 * useCardDex - 図鑑メイン状態管理フック
 *
 * 図鑑の画面状態、ナビゲーション、設定を管理する
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

// ========================================
// 画面定義
// ========================================

export const DEX_VIEWS = {
  LIST: 'list',           // カード一覧
  DETAIL: 'detail',       // カード詳細
  PROGRESS: 'progress',   // 進捗
  ACHIEVEMENTS: 'achievements', // 実績
  STATISTICS: 'statistics', // 統計
  // Phase 2.5 追加
  MATERIALS: 'materials',           // 素材一覧
  MATERIAL_DETAIL: 'materialDetail', // 素材詳細
};

// ========================================
// 初期状態
// ========================================

const initialFilters = {
  attribute: [],      // 属性フィルター
  cardType: [],       // 種類フィルター
  rarity: [],         // レアリティフィルター
  cost: [0, 10],      // コスト範囲
  costValues: [],     // コスト個別選択（DeckBuilderと同形式）
  owned: 'all',       // 所持状態: 'all' | 'owned' | 'not-owned'
  favorite: false,    // お気に入りのみ
  // 詳細フィルター（Phase 1.5追加）
  keyword: [],        // キーワード能力フィルター
  keywordMode: 'or',  // キーワード検索モード: 'or' | 'and'
  category: [],       // カテゴリフィルター
  categoryMode: 'or', // カテゴリ検索モード: 'or' | 'and'
  attack: [0, 10000], // 攻撃力範囲
  hp: [0, 10000],     // HP範囲
  effectSearch: '',   // 効果テキスト検索
  forbidden: false,   // 禁忌カードのみ
};

const initialSettings = {
  displayMode: 'grid',     // 'grid' | 'list'
  progressMode: 'species', // 'species' | 'all' (種別 / 全レアリティ)
  defaultSort: 'id',
  sortOrder: 'asc',
  showWishlistHearts: false, // ウィッシュリストハート表示
};

// ========================================
// コンテキスト
// ========================================

const CardDexContext = createContext(null);

// ========================================
// プロバイダー
// ========================================

export function CardDexProvider({ children }) {
  // 画面状態
  const [currentView, setCurrentView] = useState({
    view: DEX_VIEWS.LIST,
    params: {},
  });

  // 画面履歴（戻る機能用）
  const [viewHistory, setViewHistory] = useState([]);

  // フィルター状態
  const [filters, setFilters] = useState(initialFilters);

  // 検索状態
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');

  // 選択状態
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedRarity, setSelectedRarity] = useState('C');

  // 設定
  const [settings, setSettings] = useState(initialSettings);

  // 素材図鑑の状態（詳細画面から戻った時に復元するため）
  const [materialActiveTab, setMaterialActiveTab] = useState('attribute');
  const [materialTierFilter, setMaterialTierFilter] = useState('all');
  const [materialSearchQuery, setMaterialSearchQuery] = useState('');

  // ========================================
  // ナビゲーション関数
  // ========================================

  const navigateTo = useCallback((view, params = {}) => {
    // 現在の画面を履歴に追加
    setViewHistory((prev) => [...prev, currentView]);
    setCurrentView({ view, params });
  }, [currentView]);

  const goBack = useCallback(() => {
    if (viewHistory.length > 0) {
      const prevView = viewHistory[viewHistory.length - 1];
      setViewHistory((prev) => prev.slice(0, -1));
      setCurrentView(prevView);
      return true;
    }
    return false;
  }, [viewHistory]);

  // 便利なナビゲーション関数
  const navigateToList = useCallback(() => {
    setCurrentView({ view: DEX_VIEWS.LIST, params: {} });
    setViewHistory([]);
  }, []);

  const navigateToDetail = useCallback((cardId, rarity = 'C') => {
    navigateTo(DEX_VIEWS.DETAIL, { cardId });
    setSelectedCardId(cardId);
    setSelectedRarity(rarity);
  }, [navigateTo]);

  const navigateToProgress = useCallback(() => {
    navigateTo(DEX_VIEWS.PROGRESS);
  }, [navigateTo]);

  const navigateToAchievements = useCallback(() => {
    navigateTo(DEX_VIEWS.ACHIEVEMENTS);
  }, [navigateTo]);

  const navigateToStatistics = useCallback(() => {
    navigateTo(DEX_VIEWS.STATISTICS);
  }, [navigateTo]);

  // Phase 2.5: 素材図鑑ナビゲーション
  const navigateToMaterials = useCallback(() => {
    navigateTo(DEX_VIEWS.MATERIALS);
  }, [navigateTo]);

  const navigateToMaterialDetail = useCallback((materialId) => {
    navigateTo(DEX_VIEWS.MATERIAL_DETAIL, { materialId });
  }, [navigateTo]);

  // ========================================
  // フィルター操作
  // ========================================

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const toggleFilterValue = useCallback((key, value) => {
    setFilters((prev) => {
      const currentValues = prev[key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [key]: newValues,
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setSearchQuery('');
  }, []);

  // ========================================
  // ソート操作
  // ========================================

  const updateSort = useCallback((newSortBy, newSortOrder = null) => {
    if (newSortOrder) {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    } else {
      // 同じソートキーの場合は順序を反転
      if (sortBy === newSortBy) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(newSortBy);
        setSortOrder('asc');
      }
    }
  }, [sortBy]);

  // ========================================
  // 設定操作
  // ========================================

  const updateSettings = useCallback((newSettings) => {
    setSettings((prev) => ({
      ...prev,
      ...newSettings,
    }));
  }, []);

  const toggleDisplayMode = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      displayMode: prev.displayMode === 'grid' ? 'list' : 'grid',
    }));
  }, []);

  const toggleProgressMode = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      progressMode: prev.progressMode === 'species' ? 'all' : 'species',
    }));
  }, []);

  const toggleShowWishlistHearts = useCallback(() => {
    setSettings((prev) => ({
      ...prev,
      showWishlistHearts: !prev.showWishlistHearts,
    }));
  }, []);

  // ========================================
  // コンテキスト値
  // ========================================

  const value = {
    // 状態
    currentView,
    filters,
    searchQuery,
    sortBy,
    sortOrder,
    selectedCardId,
    selectedRarity,
    settings,
    viewHistory,

    // 素材図鑑の状態
    materialActiveTab,
    materialTierFilter,
    materialSearchQuery,

    // セッター
    setCurrentView,
    setFilters,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setSelectedCardId,
    setSelectedRarity,
    setSettings,

    // 素材図鑑のセッター
    setMaterialActiveTab,
    setMaterialTierFilter,
    setMaterialSearchQuery,

    // ナビゲーション
    navigateTo,
    goBack,
    navigateToList,
    navigateToDetail,
    navigateToProgress,
    navigateToAchievements,
    navigateToStatistics,
    navigateToMaterials,
    navigateToMaterialDetail,

    // フィルター操作
    updateFilter,
    toggleFilterValue,
    resetFilters,

    // ソート操作
    updateSort,

    // 設定操作
    updateSettings,
    toggleDisplayMode,
    toggleProgressMode,
    toggleShowWishlistHearts,
  };

  return (
    <CardDexContext.Provider value={value}>
      {children}
    </CardDexContext.Provider>
  );
}

// ========================================
// フック
// ========================================

export function useCardDex() {
  const context = useContext(CardDexContext);
  if (!context) {
    throw new Error('useCardDex must be used within CardDexProvider');
  }
  return context;
}

export default useCardDex;
